import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../utils/apiClient";
import config from "../utils/config";

export interface UpcomingAppointment {
  ID: number;
  booking_reference: string;
  customer_id: number;
  lab_id: number;
  lab_branch_id: number;
  appointment_date: string;
  time_slot_id: number;
  status: string;
  notes: string;
  total_amount: number;
  home_collection: boolean;
  collection_address: string;
  estimated_report_date: string;
  tests: {
    id: number;
    lab_test_id: number;
    product_test_id: number;
    test_name: string;
    price: number;
    discount_price: number;
  }[];
  created_at: string;
  updated_at: string;
}

interface UpcomingLabTestsState {
  upcomingTests: UpcomingAppointment[];
  loading: boolean;
  error: string | null;
}

const initialState: UpcomingLabTestsState = {
  upcomingTests: [],
  loading: false,
  error: null,
};

/** Fetch upcoming appointments (pending/confirmed) from the AMS service */
export const getUpcomingLabTests = createAsyncThunk(
  "upcomingTests/getUpcomingTests",
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(
        `${config.services.ams}/api/v1/appointments`,
        { params: { per_page: 100 } }
      );
      const all: UpcomingAppointment[] = response.data?.data || response.data || [];
      // Filter to only pending / confirmed (upcoming) appointments
      return all.filter(
        (a) => a.status === "pending" || a.status === "confirmed"
      );
    } catch (error: any) {
      const msg = error?.message || "Failed to fetch upcoming tests";
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const updateUpcomingTestStatus = createAsyncThunk(
  "upcomingTests/updateStatus",
  async ({ id, status }: { id: number; status: string }, thunkAPI) => {
    try {
      const response = await apiClient.put(
        `${config.services.ams}/api/v1/appointments/${id}/status`,
        { status }
      );
      return response.data;
    } catch (error: any) {
      const msg = error?.message || "Failed to update status";
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const confirmUpcomingTest = createAsyncThunk(
  "upcomingTests/confirm",
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.post(
        `${config.services.ams}/api/v1/appointments/${id}/confirm`
      );
      return response.data;
    } catch (error: any) {
      const msg = error?.message || "Failed to confirm appointment";
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

const UpcomingLabTestsSlice = createSlice({
  name: "upcomingTests",
  initialState,
  reducers: {
    resetUpcomingTestsState: (state) => {
      state.upcomingTests = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUpcomingLabTests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUpcomingLabTests.fulfilled, (state, action) => {
        state.loading = false;
        state.upcomingTests = action.payload;
      })
      .addCase(getUpcomingLabTests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetUpcomingTestsState } = UpcomingLabTestsSlice.actions;

export { UpcomingLabTestsSlice };
